<template>
  <div class="tile is-ancestor is-parent">
    <div class="tile is-parent is-vertical">
      <article class="tile is-child box">
        <div class="columns is-gapless">
          <div class="column">
            <p class="title">Parts</p>
            <subtitle-totals :labels="['Total']"
              :totals="[partsTotal]"
              :shows="[true]" />
          </div>
          <div v-if="!readOnlyView"
            class="column">
            <div class="control is-pulled-right">
              <div v-if="!isLockPurchaseOrder"
                class="buttons">
                <button class="button is-primary tooltip is-tooltip-topright"
                  :disabled="isLockPurchaseOrder"
                  data-tooltip="Add new part"
                  @click="toggleQuotedParts()">
                  <span class="icon">
                    <i class="mdi"
                      :class="{ 'mdi-plus' : !isQuotedPartsActive, 'mdi-minus' : isQuotedPartsActive }" />
                  </span>
                  <span>{{ isQuotedPartsActive ? 'Close' : 'Add' }}</span>
                </button>
              </div>
              <div v-else>
                <order-lock-notification />
              </div>
            </div>
          </div>
        </div>
        <quoted-parts v-if="isQuotedPartsActive"
          v-model="innerValue.parts"
          :purchase-order-id="innerValue.purchaseOrderId"
          :quote-id="innerValue.quoteId"
          :read-only-view="readOnlyView"
          @item-added="onItemAdded"
          @item-deleted="deleteAllItems" />
        <div v-if="isQuotedPartsActive"
          class="is-divider" />
        <bulma-table class="table is-bordered is-striped is-narrow is-fullwidth"
          :columns="columns"
          :page-index="1"
          :page-size="100"
          :total-rows="activeItems.length"
          :show-pagination="false"
          :is-loading="isTableLoading"
          :sort-column="sortColumn"
          :sort-order="sortOrder"
          @sort="sort">
          <tr v-for="(item, index) in activeItems"
            :key="item.purchaseOrderItemId">
            <td class="has-vertical-middle">
              {{ item.itemNo }}
              <span v-if="item.quoteItemDeleted"
                class="icon has-text-danger tooltip is-tooltip-danger"
                data-tooltip="Item has been deleted in quote">
                <i class="mdi mdi-alert mdi-18px" />
              </span>
            </td>
            <td class="has-vertical-middle itemdesc-input">{{ item.description }}</td>
            <td class="has-vertical-middle has-text-centered">
              <span class="tag"
                :class="sideTagClass(item.side)">{{ item.side }}</span>
            </td>
            <td class="has-vertical-middle">{{ item.mark }}</td>
            <td class="has-vertical-middle has-text-right qty-width">
              <span v-if="(item.quantity + item.otherPoOrderedQty) > item.quotedItemQty"
                style="padding-right: 3px"
                class="icon has-text-warning tooltip is-tooltip-warning"
                data-tooltip="Total Order Quantity is more than quoted quantity">
                <i class="mdi mdi-alert mdi-18px" />
              </span>
              <vue-numeric v-if="!readOnlyView"
                class="input has-vertical-middle has-text-right qty-input"
                v-model.number="item.quantity"
                :min="1"
                :disabled="isLockPurchaseOrder"
                :precision="0"
                @input="updateQty(index, item)" />
              <span v-else>{{ item.quantity }}</span>
            </td>
            <td class="has-vertical-middle has-text-right money-input">
              <vue-numeric v-if="!readOnlyView"
                class="input has-text-right"
                v-model.number="item.unitPrice"
                :min="0"
                :disabled="isLockPurchaseOrder"
                :precision="2"
                @input="updateValue(index, item)" />
              <span v-else>{{ item.unitPrice }}</span>
            </td>
            <td class="has-vertical-middle has-text-right total-input">{{ $filters.formatNumber(item.quantity * item.unitPrice, $userInfo.locale) }}</td>
            <td class="has-vertical-middle partno-input">{{ item.partNo }}</td>
            <td class="has-vertical-middle has-text-right">{{ item.receivedQty - item.returnQty }}</td>
            <td class="has-vertical-middle has-text-centered is-content-width">
              <button class="button is-danger is-small is-inverted"
                @click="deleteItem(item, index)"
                :disabled="readOnlyView || isLockPurchaseOrder">
                <span class="icon is-medium">
                  <i class="mdi mdi-delete mdi-24px" />
                </span>
              </button>
            </td>
          </tr>
          <template slot="empty">
            <section class="section">
              <div class="content has-text-grey has-text-centered">
                <span icon="icon is-large">
                  <i class="mdi mdi-48px mdi-emoticon-sad" />
                </span>
                <p>Nothing</p>
              </div>
            </section>
          </template>
          <template slot="footer">
            <tr class="no-border"
              :class="[partsTotal >= 0 ? `purchase-order-background-success-${$theme.name}` : `purchase-order-background-danger-${$theme.name}` ]">
              <th class="has-text-left"
                :colspan="totalColSpan">Totals</th>
              <th class="has-text-right">{{ $filters.formatCurrency(partsTotal, $userInfo.locale) }}</th>
              <th :colspan="blankColSpan" />
            </tr>
          </template>
        </bulma-table>
      </article>
    </div>
  </div>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash.debounce'
import _orderBy from 'lodash/orderBy'
import BulmaTable from '@/components/BulmaTable'
import QuotedParts from './components/QuotedParts'
import SubtitleTotals from '@/components/SubtitleTotals'
import VueNumeric from '@/components/VueNumeric'
import { EventHubTypes } from '@/enums'
import { OrderPartsColumns } from './columns'
import { roundAwayFromZero } from '@/components/utils/AccountingFunctions'
import OrderLockNotification from './components/PurchaseOrderLockNotification'

const title = 'Parts'

export default {
  name: 'PurchaseOrderParts',
  components: {
    BulmaTable,
    QuotedParts,
    VueNumeric,
    SubtitleTotals,
    OrderLockNotification
  },
  props: {
    value: null,
    readOnlyView: {
      type: Boolean
    },
    isReceive: {
      type: Boolean,
      default: false
    },
    isLockPurchaseOrder: {
      type: Boolean
    }
  },
  data() {
    return {
      innerValue: _cloneDeep(this.value),
      itemToOrder: null,
      quotedItem: null,
      isTableLoading: false,
      selectedRow: false,
      isQuotedPartsActive: false,
      sortColumn: '',
      sortOrder: ''
    }
  },
  computed: {
    columns() {
      return OrderPartsColumns
    },
    activeItems() {
      return this.innerValue.parts.filter(i => !i.deleted)
    },
    partsTotal() {
      return roundAwayFromZero(
        this.activeItems.reduce(function(total, item) {
          return total + item.dealerCost
        }, 0),
        2
      )
    },
    totalColSpan() {
      // return this.columns.length - (this.$userInfo.isSupportUser ? 4 : 5)
      return this.columns.length - 4
    },
    blankColSpan() {
      return this.columns.length - this.totalColSpan - 1
    }
  },
  watch: {
    innerValue: {
      handler: function(newVal, oldVal) {
        this.$emit('input', newVal)
      },
      deep: true
    }
  },
  beforeDestroy() {
    this.$eventHub.$off(EventHubTypes.EntitySaved)
    this.$eventHub.$off(EventHubTypes.EntityReload)
  },
  created() {},
  mounted() {
    this.$eventHub.$on(EventHubTypes.EntitySaved, () => {
      this.innerValue = _cloneDeep(this.value)
      this.isQuotedPartsActive = false
    })
    this.$eventHub.$on(EventHubTypes.EntityReload, () => {
      this.innerValue = _cloneDeep(this.value)
      this.isQuotedPartsActive = false
    })
  },
  methods: {
    toggleQuotedParts() {
      this.isQuotedPartsActive = !this.isQuotedPartsActive
    },
    onItemAdded(newItem) {
      this.innerValue.parts.splice(this.innerValue.parts.length, 1, newItem)
    },
    deleteItem(item, index) {
      if (item.isNew) {
        const itemIndex = this.innerValue.parts
          .map(function(obj) {
            return obj.purchaseOrderItemId
          })
          .indexOf(item.purchaseOrderItemId)
        if (itemIndex >= 0) {
          this.innerValue.parts.splice(itemIndex, 1)
        }
      } else {
        let netRecQty = item.receivedQty - item.returnQty
        if (netRecQty > 0 || item.returnQty > 0) {
          this.$notification.openNotificationWithType('danger', title, 'This item has been received or returned. You can\'t delete it.')
          return false
        }
        item.deleted = true
        item.isDeleted = true
      }
    },
    deleteAllItems() {
      for (var i = 0; i < this.innerValue.parts.length; i++) {
        const itemIndex = this.innerValue.parts
          .map(function(obj) {
            return obj.purchaseOrderItemId
          })
          .indexOf(this.innerValue.parts[i].purchaseOrderItemId)
        if (itemIndex >= 0) {
          if ((this.innerValue.parts[itemIndex].receivedQty - this.innerValue.parts[itemIndex].returnQty <= 0) && this.innerValue.parts[itemIndex].returnQty <= 0) {
            this.innerValue.parts[itemIndex].isDeleted = true
            this.innerValue.parts[itemIndex].deleted = true
          }
        }
      }

      var items = this.innerValue.parts
      items.reduce(function(list, item, index) {
      if (item.isDeleted && item.isNew) list.push(index)
        return list
      }, []).reverse().forEach(function(index) {
        items.splice(index,1)
      })

      this.innerValue.parts = items
    },
    sideTagClass(side) {
      let tagClass = ''
      switch (side) {
        case 'O':
          tagClass = `is-${this.$theme.name}`
          break
        case 'R':
          tagClass = 'is-primary'
          break
        case 'L':
          tagClass = 'is-info'
          break
      }
      return tagClass
    },
    updateValue: _debounce(function(index, item) {
      item.dealerCost = roundAwayFromZero(item.quantity * item.unitPrice)
    }, 500),
    updateQty: _debounce(function(index, item) {
      let netRecQty = item.receivedQty - item.returnQty
      if (item.quantity < netRecQty) {
        item.quantity = netRecQty
        this.$notification.openNotificationWithType('danger', title, 'Order Quantity cannot be less than Net Quantity Received: ' + netRecQty)
        return false
      }

      // let otherTotalOnHand = item.quantity + item.totalInStock - item.receivedQty

      // if (otherTotalOnHand > item.quotedItemQty) {
      //   if (item.isNew) {
      //     item.quantity = item.quotedItemQty - item.totalInStock
      //   }
      //   this.$notification.openNotificationWithType('warning', title, 'Order Quantity is more than quoted quantity. Please check your order.')
      //   return false
      // }
      item.dealerCost = roundAwayFromZero(item.quantity * item.unitPrice)
    }, 500),
    sort(name, order) {
      this.innerValue.parts = _orderBy(this.activeItems, name, order)
    }
  }
}
</script>

<style lang="scss" scoped>
.itemno-input {
  max-width: 6.5em;
}
.itemdesc-input {
  min-width: 15em;
}
.money-input {
  max-width: 5.5em;
}
.total-input {
  min-width: 5.5em;
}
.qty-input {
  max-width: 3.5em;
}
.partno-input {
  min-width: 6.5em;
}
.qty-width {
  max-width: 3.7em;
}
</style>